import './style.scss';

import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';

import { AbilityContext, Can } from 'components/Can';
import Pagination from 'components/Pagination';

import { getAll as getAllEmployees, getAllRoles, exportExcel } from 'apis/employees.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import {
  getAcadamicLevelShortHand,
  handleEnterKeyPress,
  mapError,
  sanitizeObject,
} from 'utils/func';

import { SORTED } from 'constant';
import AddForm from './components/AddForm';
import ConfirmDelete from './components/ConfirmDelete';
import ConfirmResetPassword from './components/ConfirmResetPassword';
import ViewDetail from './components/ViewDetail';

export default function EmployeeViewList() {
  // #region Data
  const { t } = useTranslation();

  const ability = useContext(AbilityContext);

  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    first: parseInt(searchParams.get('first'), 10) || 0,
    rowsPerPage: 10,
  });
  const [isCreateMode, setIsCreateMode] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [innerWidth, setInnerWidth] = useState(0);
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filtersTable, setFiltersTable] = useState({
    _sort: { value: null },
    _role: { value: null },
    _email: { value: null },
    _fullname: { value: null },
    _organization: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    sort: null,
    roles: null,
    email: null,
    fullname: null,
    organizationId: null,
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    sort: null,
    roles: null,
    email: null,
    fullname: null,
    organizationId: null,
  });

  const {
    data: _data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ['employees', pagination, appliedFilters], // note that appliedFilters is used here
    () => getAllEmployees(pagination, sanitizeObject(appliedFilters)),
    {
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  const data = useMemo(
    () => _data?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_data]
  );

  const { data: rolesData } = useQuery(['roles'], getAllRoles);
  const roles = useMemo(
    () =>
      rolesData?.data.map((e) => ({
        ...e,
        name: e.code ? t(`userRole.${e.code}`) : e.name,
      })) ?? [],
    [rolesData, t]
  );

  const { data: organizationsData } = useQuery(['organizations'], getAllOrganizations);
  const organizations = useMemo(
    () =>
      organizationsData?.data?.map((e) => ({
        id: e?.id,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const statuses = t('employee.viewList.statuses', { returnObjects: true });
  // #endregion

  // #region Event
  useEffect(() => {
    const localFilters = JSON.parse(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-filters`) ??
        '{ "roles" : [], "email" : "", "fullname" : "" }'
    );
    // two types of filters must set to the same value
    setFilters(localFilters);
    setAppliedFilters(localFilters);

    // Set rows per page in localStorage
    const localRowsPerPage = parseInt(
      localStorage.getItem(`admin-${location.pathname.slice(1)}-rowsPerPage`),
      10
    );
    if (localRowsPerPage) {
      setPagination((cur) => ({ ...cur, rowsPerPage: localRowsPerPage }));
    }
    // Set current page (first) with searchParams
    const currentFirst = parseInt(searchParams.get('first'), 10);
    if (currentFirst) {
      setPagination((cur) => ({ ...cur, first: currentFirst }));
    }
    setIsLoadedFilterFromLocalStorage(true);
  }, []);

  useLayoutEffect(() => {
    const updateWidth = () => {
      setInnerWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateWidth);
    updateWidth();
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  // #endregion
  // eslint-disable-next-line react/prop-types
  const formatIndex = (value, props) => props.rowIndex + pagination.first + 1;

  const formatEmail = (value) =>
    value?.email ? (
      <>
        {value?.email.split('@')[0]}
        <wbr />@{value?.email.split('@')[1]}
      </>
    ) : (
      ''
    );

  const formatRoles = ({ userRoles }) =>
    userRoles
      ?.slice()
      ?.sort((a, b) => a.role.code.localeCompare(b.role.code))
      ?.map((item) => t(`userRole.${item?.role?.code}`))
      ?.join('\n');

  const formatFullname = (value) => {
    const publicProfileId = value?.contacts?.[0]?.scientificProfile?.publicProfile?.publicProfileId;
    if (publicProfileId) {
      return (
        <a
          href={`${process.env.REACT_APP_SCIENTIST_URL}/profile/${publicProfileId}`}
          target="_blank"
          rel="noreferrer"
        >
          <div>
            {getAcadamicLevelShortHand(
              value?.contacts?.[0]?.scientificProfile?.academicRank,
              value?.contacts?.[0]?.scientificProfile?.academicDegree,
              t,
              true
            )}
            {value?.contacts?.[0]?.fullname}
          </div>
        </a>
      );
    }

    return (
      <div>
        {getAcadamicLevelShortHand(
          value?.contacts?.[0]?.scientificProfile?.academicRank,
          value?.contacts?.[0]?.scientificProfile?.academicDegree,
          t,
          true
        )}
        {value?.contacts?.[0]?.fullname}
      </div>
    );
  };

  const formatOrganization = (value) => {
    if (value?.contacts?.[0]?.organization?.englishName === 'Other') {
      return value?.contacts?.[0]?.specificOrganizationName;
    }
    return t('lang') === 'vi'
      ? value?.contacts?.[0]?.organization?.name
      : value?.contacts?.[0]?.organization?.englishName;
  };

  const formatStatus = (value) => {
    if (!value?.status) return '';

    const statusColorMapping = {
      inactive: 'warning',
      active: 'success',
      blocked: 'danger',
      pending: 'info',
    };

    return (
      <Tag
        className="w-7rem"
        severity={statusColorMapping[value.status]}
        value={statuses[value.status]}
        rounded
      />
    );
  };

  const formatSortedIcon = (field) => {
    const sortField = filters?.sort?.filter((item) => item.field === field)?.[0]?.order || null;
    if (sortField === SORTED.ASSENDING) return 'pi pi-sort-alpha-down';
    if (sortField === SORTED.DESCENDING) return 'pi pi-sort-alpha-up';
    return 'pi pi-sort-alt';
  };

  const handleKeyDown = useCallback(
    (e) => {
      handleEnterKeyPress(e, () => {
        setAppliedFilters(filters);
      });
    },
    [filters]
  );

  const fullnameFilter = () => (
    <InputText
      value={filters.fullname}
      onChange={(e) => {
        const newFilters = { ...filters, fullname: e.target.value };
        setFilters(newFilters);
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      onKeyDown={handleKeyDown}
      placeholder={t('employee.viewList.searchByFullname')}
      className="p-column-filter"
    />
  );

  const emailFilter = () => (
    <InputText
      value={filters.email}
      onChange={(e) => {
        const newFilters = { ...filters, email: e.target.value };
        setFilters(newFilters);
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      onKeyDown={handleKeyDown}
      placeholder={t('employee.viewList.searchByEmail')}
      className="p-column-filter"
    />
  );

  const organizationFilter = () => (
    <MultiSelect
      value={filters.organizationId}
      options={organizations}
      onChange={(e) => {
        const newFilters = { ...filters, organizationId: e.value };
        setFilters(newFilters);
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      optionLabel="name"
      optionValue="id"
      filter
      showClear
      placeholder={t('employee.viewList.searchByOrganization')}
      maxSelectedLabels={3}
      style={{ maxWidth: '25rem', width: '20rem' }}
    />
  );

  const rolesFilter = () => (
    <MultiSelect
      value={filters.roles}
      options={roles}
      onChange={(e) => {
        const newFilters = { ...filters, roles: e.value };
        setFilters(newFilters);
        localStorage.setItem(
          `admin-${location.pathname.slice(1)}-filters`,
          JSON.stringify(newFilters)
        );
      }}
      optionLabel="name"
      optionValue="code"
      showClear
      filter
      placeholder={t('employee.viewList.searchByRoles')}
      style={{ minWidth: '8rem' }}
      maxSelectedLabels={3}
    />
  );

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={options.filterClearCallback}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={options.filterApplyCallback}
      severity="success"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && filters[field]?.length > 0 ? { color: '#22C55E' } : { color: '#6c757d' };

  const formatActions = (value) => (
    <div className="flex justify-content-center align-items-center gap-1">
      <Can do="read_detail" on="employee">
        <Button
          icon="pi pi-eye"
          severity="info"
          tooltip={t('employee.view.title')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedEmployee(value);
            setIsViewModalOpen(true);
          }}
        />
      </Can>
      <Can do="update" on="employee">
        <Button
          icon="pi pi-pencil"
          className="bg-orange-600 border-orange-600"
          tooltip={t('employee.update.title')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedEmployee(value);
            setIsCreateMode(false);
            setIsAddModalOpen(true);
          }}
        />
      </Can>
      <Can do="reset_password" on="employee">
        <Button
          severity="help"
          icon="pi pi-replay"
          className="bg-purple-700 border-purple-700"
          tooltip={t('employee.resetPassword.title')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedEmployee(value);
            setIsResetPasswordModalOpen(true);
          }}
        />
      </Can>
      <Can do="delete" on="employee">
        <Button
          icon="pi pi-trash"
          className="bg-red-600 border-red-600"
          tooltip={t('employee.delete.title')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => {
            setSelectedEmployee(value);
            setIsDeleteModalOpen(true);
          }}
        />
      </Can>
    </div>
  );

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const response = await exportExcel();
      if (!response?.data) return;

      const file = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employees.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // handle error silently
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (field) => {
    const newFilters = { ...filters };
    // [{ field: 'fullname', order: 'asc' }, { field: 'email', order: 'desc' }]
    if (!newFilters.sort) {
      newFilters.sort = [{ field, order: SORTED.ASSENDING }];
    } else if (Array.isArray(newFilters.sort)) {
      const index = newFilters.sort.findIndex((e) => e.field === field);
      if (index === -1) {
        newFilters.sort.push({ field, order: SORTED.ASSENDING });
      } else if (newFilters.sort[index]?.order === SORTED.ASSENDING) {
        newFilters.sort[index].order = SORTED.DESCENDING;
      } else if (newFilters.sort[index]?.order === SORTED.DESCENDING) {
        newFilters.sort = newFilters.sort.filter((e) => e.field !== field);
      }
    }
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(newFilters));
  };

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">{t('employee.viewList.title')}</h4>
        <div className="flex gap-2">
          <Can do="export_excel" on="employee">
            <Button
              className="bg-green-600 border-green-600"
              label="Export Excel"
              icon="pi pi-file-excel"
              loading={isExporting}
              onClick={handleExportExcel}
            />
          </Can>
          <Can do="create" on="employee">
            <Button
              className="bg-cyan-600 border-cyan-600"
              label={t('employee.viewList.add')}
              icon="pi pi-plus"
              onClick={() => {
                setSelectedEmployee(null);
                setIsCreateMode(true);
                setIsAddModalOpen(true);
              }}
            />
          </Can>
        </div>
      </div>

      <DataTable
        value={data.data}
        className="p-datatable-gridlines header-table"
        showGridlines
        scrollable
        scrollHeight="calc(100vh - 14rem - 120px)"
        dataKey="id"
        filters={filtersTable}
        filterDisplay="menu"
        onFilter={(e) => setFiltersTable(e.filters)}
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('employee.viewList.emptyMessage')}
      >
        <Column
          body={formatIndex}
          header={t('employee.viewList.no')}
          className="text-center w-4rem"
        />
        <Column
          field="contacts.0.fullname"
          header={
            <div className="cursor-pointer" onClick={() => handleSort('fullname')}>
              {t('employee.fullname')}
              <span className="ml-2">
                <i className={formatSortedIcon('fullname')} />
              </span>
            </div>
          }
          filter
          filterField="_fullname"
          filterElement={fullnameFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, fullname: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          body={formatFullname}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          headerStyle={filterActiveStyle('fullname')}
        />
        <Column
          header={
            <div className="cursor-pointer" onClick={() => handleSort('email')}>
              {t('employee.email')}
              <span className="ml-2 ">
                <i className={formatSortedIcon('email')} />
              </span>
            </div>
          }
          body={formatEmail}
          filter
          filterField="_email"
          filterElement={emailFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, email: '' };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          headerStyle={filterActiveStyle('email')}
        />
        <Column
          hidden={innerWidth < 1200}
          field="contacts.0.scientificProfile.personalPhone"
          header={t('employee.phone')}
          className="w-10rem text-center"
        />
        <Column
          header={t('employee.organization')}
          body={formatOrganization}
          filter
          filterField="_organization"
          filterElement={organizationFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu={organizations?.length > 0}
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, organizationId: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          className="w-9rem text-center"
          headerStyle={filterActiveStyle('organizationId')}
          style={{ whiteSpace: 'pre-line' }}
        />
        <Column
          header={t('employee.roles')}
          body={formatRoles}
          filter={ability.can('update', 'employee')}
          filterField="_role"
          filterElement={rolesFilter}
          filterClear={filterClearTemplate}
          filterApply={filterApplyTemplate}
          showFilterMenu
          showFilterMatchModes={false}
          onFilterClear={() => {
            const newFilters = { ...filters, roles: [] };
            setFilters(newFilters);
            setAppliedFilters(newFilters);
            localStorage.setItem(
              `admin-${location.pathname.slice(1)}-filters`,
              JSON.stringify(newFilters)
            );
          }}
          onFilterApplyClick={() => {
            setAppliedFilters(filters);
          }}
          className="text-center"
          headerStyle={filterActiveStyle('roles')}
          style={{ whiteSpace: 'pre-line' }}
        />
        <Column
          header={t('employee.viewList.status')}
          body={formatStatus}
          className="w-6rem text-center"
        />
        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          className="w-8rem text-center"
        />
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={data.pagination}
        pageName="employee"
      />

      <AddForm
        selectedEmployee={selectedEmployee}
        visible={isAddModalOpen}
        setVisible={setIsAddModalOpen}
        isCreateMode={isCreateMode}
        refetch={refetch}
      />

      <Can do="read_detail" on="employee">
        <ViewDetail
          selectedEmployee={selectedEmployee}
          visible={isViewModalOpen}
          setVisible={setIsViewModalOpen}
          statuses={statuses}
        />
      </Can>

      <Can do="delete" on="employee">
        <ConfirmDelete
          selectedEmployee={selectedEmployee}
          visible={isDeleteModalOpen}
          setVisible={setIsDeleteModalOpen}
          refetch={refetch}
        />
      </Can>

      <Can do="reset_password" on="employee">
        <ConfirmResetPassword
          selectedEmployee={selectedEmployee}
          visible={isResetPasswordModalOpen}
          setVisible={setIsResetPasswordModalOpen}
          refetch={refetch}
        />
      </Can>
    </div>
  );
}
